import React, { useState, useEffect } from 'react';
import AdminCommissions from './AdminCommissions';
import AdminMemeBotSettings from './AdminMemeBotSettings';
import AdminMultiAssetBotSettings from './AdminMultiAssetBotSettings';
import AdminScalpingSettings from './AdminScalpingSettings';
import AdminDiscScalpingSettings from './AdminDiscScalpingSettings';
import AdminFundingCarrySettings from './AdminFundingCarrySettings';
import AdminSingleAssetHL from './AdminSingleAssetHL';
import AdminBackups from './AdminBackups';
import AdminEmail from './AdminEmail';
import './Admin.css';
import axios from 'axios'; 

const Admin = () => {
  const [activeTab, setActiveTab] = useState(
    () => sessionStorage.getItem('adminActiveTab') || 'stats'
  );
  const [isBotAdmin, setIsBotAdmin] = useState(false);
  const [isAdminUser, setIsAdminUser] = useState(false);
  const [stats, setStats] = useState(null);
  const [packages, setPackages] = useState([]);
  const [users, setUsers] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]); 
  const [tokens, setTokens] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userTokens, setUserTokens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showPackageForm, setShowPackageForm] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);

  const API_ORIGIN = process.env.REACT_APP_API_URL || 'https://trading.omniscalping.com';
  
  const getUserIdFromToken = () => {
    const token = localStorage.getItem('sessionToken');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.userId || payload.id;
      } catch (e) {
        console.error('Error decoding token:', e);
        return null;
      }
    }
    return null;
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('sessionToken');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  };

  useEffect(() => {
    const applyFlags = (isAdmin, isBot) => {
      if (isAdmin) setIsAdminUser(true);
      if (isBot) setIsBotAdmin(true);
    };

    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      applyFlags(!!u.isAdmin, !!u.isBotAdmin);
    } catch {
      /* ignore */
    }

    try {
      const t = localStorage.getItem('sessionToken');
      if (t) {
        const p = JSON.parse(atob(t.split('.')[1]));
        applyFlags(!!p.isAdmin, !!p.isBotAdmin);
      }
    } catch {
      /* ignore */
    }

    const token = localStorage.getItem('sessionToken');
    if (token) {
      fetch(`${API_ORIGIN}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          if (data?.user) applyFlags(data.user.isAdmin, data.user.isBotAdmin);
        })
        .catch(() => {});
    }
  }, [API_ORIGIN]);

  useEffect(() => {
    sessionStorage.setItem('adminActiveTab', activeTab);
  }, [activeTab]);

  // Fetch stats on mount
  useEffect(() => {
    if (activeTab === 'stats') {
      fetchStats();
    } else if (activeTab === 'packages') {
      fetchPackages();
    } else if (activeTab === 'users') {
      fetchUsers();    
	} else if (activeTab === 'subscriptions') {
      fetchSubscriptions();
    } else if (activeTab === 'tokens') {
      fetchTokens();
	}
  }, [activeTab]);

  

  // ============================================================================
  // STATS
  // ============================================================================
  const fetchStats = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_ORIGIN}/api/admin/stats`, {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error('Failed to fetch stats');
      }

      const data = await response.json();
      setStats(data.stats);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ============================================================================
  // PACKAGES
  // ============================================================================
  const fetchPackages = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_ORIGIN}/api/admin/packages`, {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error('Failed to fetch packages');
      }

      const data = await response.json();
      setPackages(data.packages);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

    const togglePackageStatus = async (packageId, currentStatus) => {
  const action = currentStatus ? 'disable' : 'enable';
  if (!window.confirm(`Are you sure you want to ${action} this package?`)) {
    return;
  }

  try {
    const response = await fetch(`${API_ORIGIN}/api/admin/packages/${packageId}/status`, {  // ← ADD /status
      method: 'PATCH',  // ← CHANGE PUT to PATCH
      headers: getAuthHeaders(),
      body: JSON.stringify({ is_active: currentStatus ? 0 : 1 })
    });

    if (!response.ok) {
      throw new Error(`Failed to ${action} package`);
    }

    alert(`Package ${action}d successfully`);
    fetchPackages();
  } catch (err) {
    console.error(`Error ${action}ing package:`, err);
    alert(`Failed to ${action} package`);
  }
};


  const savePackage = async (packageData) => {
    try {
      const url = editingPackage 
        ? `${API_ORIGIN}/api/admin/packages/${editingPackage.id}`
        : `${API_ORIGIN}/api/admin/packages`;

      const method = editingPackage ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method: method,
        headers: getAuthHeaders(),
        body: JSON.stringify(packageData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to save package');
      }

      alert(editingPackage ? 'Package updated!' : 'Package created!');
      setShowPackageForm(false);
      setEditingPackage(null);
      fetchPackages();
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };
  
 // ============================================================================
// SUBSCRIPTIONS
// ============================================================================
const fetchSubscriptions = async () => {
  setLoading(true);
  setError(null);
  try {
    const response = await fetch(`${API_ORIGIN}/api/admin/subscriptions`, {
      headers: getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error('Failed to fetch subscriptions');
    }

    const data = await response.json();
    setSubscriptions(data.subscriptions);
  } catch (err) {
    setError(err.message);
  } finally {
    setLoading(false);
  }
};

// ============================================================================
// TOKENS
// ============================================================================
const fetchTokens = async () => {
  setLoading(true);
  setError(null);
  try {
    const response = await fetch(`${API_ORIGIN}/api/admin/tokens`, {
      headers: getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error('Failed to fetch tokens');
    }

    const data = await response.json();
    setTokens(data.tokens);
  } catch (err) {
    setError(err.message);
  } finally {
    setLoading(false);
  }
};

const revokeTokenAdmin = async (tokenId) => {
  if (!window.confirm('Are you sure you want to revoke this token? The user will be logged out.')) {
    return;
  }

  try {
    const response = await fetch(`${API_ORIGIN}/api/admin/tokens/${tokenId}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error('Failed to revoke token');
    }

    alert('Token revoked successfully');
    fetchTokens();
  } catch (err) {
    alert('Error: ' + err.message);
  }
};


  // ============================================================================
  // USERS
  // ============================================================================
  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const url = searchTerm 
        ? `${API_ORIGIN}/api/admin/users?search=${encodeURIComponent(searchTerm)}`
        : `${API_ORIGIN}/api/admin/users`;

      const response = await fetch(url, {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }

      const data = await response.json();
      setUsers(data.users);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const viewUserDetails = async (userId) => {
    try {
      const response = await fetch(`${API_ORIGIN}/api/admin/users/${userId}`, {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error('Failed to fetch user details');
      }

      const data = await response.json();
      setSelectedUser(data);

      // Fetch user tokens
      const tokensResponse = await fetch(`${API_ORIGIN}/api/admin/users/${userId}/tokens`, {
        headers: getAuthHeaders()
      });

      if (tokensResponse.ok) {
        const tokensData = await tokensResponse.json();
        setUserTokens(tokensData.tokens);
      }
    } catch (err) {
	  console.error('Error fetching user details:', err);	
      alert('Error: ' + err.message);
    }
  };

  const revokeToken = async (tokenId) => {
    if (!window.confirm('Are you sure you want to revoke this token?')) {
      return;
    }

    try {
      const response = await fetch(`${API_ORIGIN}/api/admin/tokens/${tokenId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error('Failed to revoke token');
      }

      alert('Token revoked successfully');

      // Refresh user details
      if (selectedUser) {
        viewUserDetails(selectedUser.user.id);
      }
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const revokeAllTokens = async (userId) => {
    if (!window.confirm('Are you sure you want to revoke ALL tokens for this user?')) {
      return;
    }

    try {
      const response = await fetch(`${API_ORIGIN}/api/admin/users/${userId}/tokens`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error('Failed to revoke tokens');
      }

      const result = await response.json();
      alert(result.message);

      // Refresh user details
      viewUserDetails(userId);
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const toggleUserAdmin = async (userId, currentAdminStatus) => {
    const action = currentAdminStatus ? 'remove admin from' : 'make admin';
    if (!window.confirm(`Are you sure you want to ${action} this user?`)) {
      return;
    }

    try {
      const response = await fetch(`${API_ORIGIN}/api/admin/users/${userId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ is_admin: !currentAdminStatus })
      });

      if (!response.ok) {
        throw new Error('Failed to update user');
      }

      alert('User updated successfully');
      fetchUsers();

      if (selectedUser && selectedUser.user.id === userId) {
        viewUserDetails(userId);
      }
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const toggleUserBotAdmin = async (userId, currentStatus) => {
    const action = currentStatus ? 'remove bot admin from' : 'grant bot admin to';
    if (!window.confirm(`Are you sure you want to ${action} this user?`)) {
      return;
    }

    try {
      const response = await fetch(`${API_ORIGIN}/api/admin/users/${userId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ is_bot_admin: !currentStatus })
      });

      if (!response.ok) {
        throw new Error('Failed to update user');
      }

      alert('User updated successfully');
      fetchUsers();

      if (selectedUser && selectedUser.user.id === userId) {
        viewUserDetails(userId);
      }
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="admin-container">
      <h1>Admin Dashboard</h1>

      {/* Tab Navigation */}
      <div className="admin-tabs" role="tablist">
        <button type="button" role="tab" className={activeTab === 'stats' ? 'active' : ''} onClick={() => setActiveTab('stats')}>
          <span className="admin-tab-icon" aria-hidden="true">📊</span>
          <span className="admin-tab-label">Dashboard</span>
        </button>
        <button type="button" role="tab" className={activeTab === 'packages' ? 'active' : ''} onClick={() => setActiveTab('packages')}>
          <span className="admin-tab-icon" aria-hidden="true">📦</span>
          <span className="admin-tab-label">Packages</span>
        </button>
        <button type="button" role="tab" className={activeTab === 'subscriptions' ? 'active' : ''} onClick={() => setActiveTab('subscriptions')}>
          <span className="admin-tab-icon" aria-hidden="true">💳</span>
          <span className="admin-tab-label">Subscriptions</span>
        </button>
        <button type="button" role="tab" className={activeTab === 'tokens' ? 'active' : ''} onClick={() => setActiveTab('tokens')}>
          <span className="admin-tab-icon" aria-hidden="true">🔑</span>
          <span className="admin-tab-label">Secure Passes</span>
        </button>
        <button type="button" role="tab" className={activeTab === 'users' ? 'active' : ''} onClick={() => setActiveTab('users')}>
          <span className="admin-tab-icon" aria-hidden="true">👥</span>
          <span className="admin-tab-label">Users</span>
        </button>
        <button type="button" role="tab" className={activeTab === 'commissions' ? 'active' : ''} onClick={() => setActiveTab('commissions')}>
          <span className="admin-tab-icon" aria-hidden="true">💰</span>
          <span className="admin-tab-label">Commissions</span>
        </button>
        {isBotAdmin && (
          <button type="button" role="tab" className={activeTab === 'meme-bots' ? 'active' : ''} onClick={() => setActiveTab('meme-bots')}>
            <span className="admin-tab-icon" aria-hidden="true">🤖</span>
            <span className="admin-tab-label">Meme bots</span>
          </button>
        )}
        <button type="button" role="tab" className={activeTab === 'multi-asset-obot' ? 'active' : ''} onClick={() => setActiveTab('multi-asset-obot')}>
          <span className="admin-tab-icon" aria-hidden="true">📈</span>
          <span className="admin-tab-label">Multi-asset</span>
        </button>
        <button type="button" role="tab" className={activeTab === 'single-asset-hl' ? 'active' : ''} onClick={() => setActiveTab('single-asset-hl')}>
          <span className="admin-tab-icon" aria-hidden="true">📊</span>
          <span className="admin-tab-label">Single Asset</span>
        </button>
        <button type="button" role="tab" className={activeTab === 'scalping' ? 'active' : ''} onClick={() => setActiveTab('scalping')}>
          <span className="admin-tab-icon" aria-hidden="true">⚡</span>
          <span className="admin-tab-label">Scalping</span>
        </button>
        <button type="button" role="tab" className={activeTab === 'disc-scalping' ? 'active' : ''} onClick={() => setActiveTab('disc-scalping')}>
          <span className="admin-tab-icon" aria-hidden="true">🎯</span>
          <span className="admin-tab-label">Disc. Scalping</span>
        </button>
        <button type="button" role="tab" className={activeTab === 'funding-carry' ? 'active' : ''} onClick={() => setActiveTab('funding-carry')}>
          <span className="admin-tab-icon" aria-hidden="true">⚡</span>
          <span className="admin-tab-label">oveBot</span>
        </button>
        {isAdminUser && (
          <button type="button" role="tab" className={activeTab === 'email' ? 'active' : ''} onClick={() => setActiveTab('email')}>
            <span className="admin-tab-icon" aria-hidden="true">📧</span>
            <span className="admin-tab-label">Email</span>
          </button>
        )}
        {isAdminUser && (
          <button type="button" role="tab" className={activeTab === 'backups' ? 'active' : ''} onClick={() => setActiveTab('backups')}>
            <span className="admin-tab-icon" aria-hidden="true">💾</span>
            <span className="admin-tab-label">Backups</span>
          </button>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="error-banner">
          ⚠️ {error}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="loading-spinner">Loading...</div>
      )}

      {/* Stats Tab */}
      {activeTab === 'stats' && stats && (
  <div className="stats-section">
    <div className="stats-grid">
      <div className="stat-card clickable" onClick={() => setActiveTab('users')}>
        <h3>Total Users</h3>
        <div className="stat-value">{stats.total_users}</div>
      </div>
      <div className="stat-card clickable" onClick={() => setActiveTab('packages')}>
        <h3>Active Packages</h3>
        <div className="stat-value">{stats.total_packages}</div>
      </div>
      <div className="stat-card clickable" onClick={() => setActiveTab('subscriptions')}>
        <h3>Active Subscriptions</h3>
        <div className="stat-value">{stats.active_subscriptions}</div>
      </div>
      <div className="stat-card clickable" onClick={() => setActiveTab('tokens')}>
        <h3>Active Secure Passes</h3>
        <div className="stat-value">{stats.active_tokens}</div>
        </div>
      <div className="stat-card clickable" onClick={() => setActiveTab('users')}>
        <h3>New Users (7 days)</h3>
        <div className="stat-value">{stats.recent_registrations}</div>
      </div>
    </div>

    <div className="methodology-breakdown">
      <h3>Subscriptions by Methodology</h3>
      <div className="methodology-list">
        {stats.subscriptions_by_methodology.map((item, idx) => (
          <div key={idx} className="methodology-item">
            <span className="methodology-name">{item.methodology}</span>
            <span className="methodology-count">{item.count}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
)}

      {/* Packages Tab */}
      {activeTab === 'packages' && (
        <div className="packages-section">
          <div className="section-header">
            <h2>Package Management</h2>
            <button 
              className="btn-primary"
              onClick={() => {
                setEditingPackage(null);
                setShowPackageForm(true);
              }}
            >
              + Create Package
            </button>
          </div>

          {showPackageForm && (
            <PackageForm 
              package={editingPackage}
              onSave={savePackage}
              onCancel={() => {
                setShowPackageForm(false);
                setEditingPackage(null);
              }}
            />
          )}

          <div className="packages-table">
            <table>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Methodology</th>
                  <th>Level</th>
                  <th>AI Package</th>
                  <th>Monthly $</th>
                  <th>Yearly $</th>
                  <th>Max Bots</th>
                  <th>Active</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {packages.map(pkg => (
                  <tr key={pkg.id}>
                    <td>{pkg.package_code}</td>
                    <td>{pkg.methodology}</td>
                    <td>{pkg.subscription_level}</td>
                    <td>{pkg.ai_package}</td>
                    <td>${pkg.monthly_price}</td>
                    <td>${pkg.yearly_price}</td>
                    <td>{pkg.max_bots}</td>
                    <td>{pkg.is_active ? '✅' : '❌'}</td>
                    <td>
                      <button 
                        className="btn-small"
                        onClick={() => {
                          setEditingPackage(pkg);
                          setShowPackageForm(true);
                        }}
                      >
                        Edit
                      </button>
                      <button 
                        className={`btn-small ${pkg.is_active ? 'btn-danger' : 'btn-success'}`}
                        onClick={() => togglePackageStatus(pkg.id, pkg.is_active)}
                      >
                        {pkg.is_active ? 'Disable' : 'Enable'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="users-section">
          <div className="section-header">
            <h2>User Management</h2>
            <div className="search-box">
              <input 
                type="text"
                placeholder="Search by email or Discord ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && fetchUsers()}
              />
              <button onClick={fetchUsers}>Search</button>
            </div>
          </div>

          <div className="users-table">
            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Email</th>
                  <th>Discord ID</th>
                  <th>Admin</th>
                  <th>Bot admin</th>
                  <th>Subscriptions</th>
                  <th>Devices</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user.id}>
                    <td>{user.id}</td>
                    <td>{user.email || 'N/A'}</td>
                    <td>{user.discord_id || 'N/A'}</td>
                    <td>{user.is_admin ? '👑' : '-'}</td>
                    <td>{user.is_bot_admin ? '🤖' : '-'}</td>
                    <td>{user.subscription_count}</td>
                    <td>{user.device_count}</td>
                    <td>{new Date(user.created_at).toLocaleDateString()}</td>
                    <td>
                      <button 
                        className="btn-small"
                        onClick={() => viewUserDetails(user.id)}
                      >
                        View
                      </button>
                      <button 
                        className="btn-small"
                        onClick={() => toggleUserAdmin(user.id, user.is_admin)}
                      >
                        {user.is_admin ? 'Remove Admin' : 'Make Admin'}
                      </button>
                      <button 
                        className="btn-small"
                        onClick={() => toggleUserBotAdmin(user.id, user.is_bot_admin)}
                      >
                        {user.is_bot_admin ? 'Remove Bot Admin' : 'Make Bot Admin'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          
        </div>
      )}

      {/* Subscriptions Tab */}
      {activeTab === 'subscriptions' && (
        <div className="subscriptions-section">
          <h2>💳 Active Subscriptions</h2>
          
          <div className="subscriptions-table">
            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>User Email</th>
                  <th>Package</th>
                  <th>Methodology</th>
                  <th>Level</th>
                  <th>Status</th>
                  <th>Started</th>
                  <th>Expires</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {subscriptions.map(sub => (
                  <tr key={sub.id}>
                    <td>{sub.id}</td>
                    <td>{sub.user_email}</td>
                    <td>{sub.package_code}</td>
                    <td>{sub.methodology}</td>
                    <td>{sub.subscription_level}</td>
                    <td>
                      <span className={`status-badge ${sub.status}`}>
                        {sub.status === 'trial' ? '🎁 Trial' : sub.status === 'active' ? '✅ Active' : '❌ Expired'}
                      </span>
                    </td>
                    <td>{new Date(sub.started_at).toLocaleDateString()}</td>
                    <td>{new Date(sub.expires_at).toLocaleDateString()}</td>
                    <td>
                      <button 
                        className="btn-small"
                        onClick={() => viewUserDetails(sub.user_id)}
                      >
                        View User
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {subscriptions.length === 0 && !loading && (
              <div className="empty-state">No active subscriptions</div>
            )}
          </div>
        </div>
      )}

            {/* Tokens Tab */}
      {activeTab === 'tokens' && (
        <div className="tokens-section">
          <h2>🔑 Active Device Passes</h2>
          
          <div className="tokens-table">
            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>User Email</th>
                  <th>Device Name</th>
                  <th>Fingerprint</th>
                  <th>Created</th>
                  <th>Last Used</th>
                  <th>Expires</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tokens.map(token => (
                  <tr key={token.id}>
                    <td>{token.id}</td>
                    <td>{token.user_email}</td>
                    <td>{token.device_name}</td>
                    <td>
                      <span className="fingerprint-short" title={token.device_fingerprint}>
                        {token.device_fingerprint.substring(0, 20)}...
                      </span>
                    </td>
                    <td>{new Date(token.created_at).toLocaleDateString()}</td>
                    <td>
                      {token.last_used ? new Date(token.last_used).toLocaleString() : 'Never'}
                    </td>
                    <td>{new Date(token.expires_at).toLocaleDateString()}</td>
                    <td>
                      <span className={`status-badge ${token.status}`}>
                        {token.status === 'active' ? '✅ Active' : '🚫 Revoked'}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        {token.status === 'active' && (
                          <button 
                            className="btn-small btn-danger"
                            onClick={() => revokeTokenAdmin(token.id)}
                          >
                            Revoke
                          </button>
                        )}
                        <button 
                          className="btn-small"
                          onClick={() => viewUserDetails(token.user_id)}
                        >
                          View User
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {tokens.length === 0 && !loading && (
              <div className="empty-state">No active tokens</div>
            )}
          </div>
        </div>
      )}

      {/* ========================================== */}
      {/* ADD COMMISSIONS TAB HERE                   */}
      {/* ========================================== */}
      {activeTab === 'commissions' && (
        <div className="commissions-section">
          <AdminCommissions user={{ id: getUserIdFromToken() }} />
        </div>
      )}

      {activeTab === 'meme-bots' && isBotAdmin && (
        <div className="meme-bot-admin-section">
          <AdminMemeBotSettings />
        </div>
      )}

      {activeTab === 'multi-asset-obot' && (
        <div className="meme-bot-admin-section">
          <AdminMultiAssetBotSettings />
        </div>
      )}

      {activeTab === 'single-asset-hl' && (
        <div className="meme-bot-admin-section">
          <AdminSingleAssetHL />
        </div>
      )}

      {activeTab === 'scalping' && (
        <div className="meme-bot-admin-section">
          <AdminScalpingSettings />
        </div>
      )}

      {activeTab === 'disc-scalping' && (
        <div className="meme-bot-admin-section">
          <AdminDiscScalpingSettings />
        </div>
      )}

      {activeTab === 'funding-carry' && (
        <div className="meme-bot-admin-section">
          <AdminFundingCarrySettings />
        </div>
      )}

      {activeTab === 'email' && isAdminUser && (
        <div className="meme-bot-admin-section">
          <AdminEmail />
        </div>
      )}

      {activeTab === 'backups' && isAdminUser && (
        <div className="meme-bot-admin-section">
          <AdminBackups />
        </div>
      )}

      {/* User Details Modal */}
      {selectedUser && (
        <UserDetailsModal 
          user={selectedUser}
          tokens={userTokens}
          onClose={() => {
            setSelectedUser(null);
            setUserTokens([]);
          }}
          onRevokeToken={revokeToken}
          onRevokeAllTokens={revokeAllTokens}
          onToggleBotAdmin={toggleUserBotAdmin}
        />
      )}
    </div>
  );
};







// ============================================================================
// PACKAGE FORM COMPONENT
// ============================================================================
const PackageForm = ({ package: pkg, onSave, onCancel }) => {
  const [formData, setFormData] = useState(pkg ? {
    ...pkg,
    commission_custom: pkg.commission_custom || '',
    profit_custom: pkg.profit_custom || '',
    bot_custom: pkg.bot_custom || '',
    monthly_price: pkg.monthly_price ?? '',
    yearly_price: pkg.yearly_price ?? '',
    custom_addon: pkg.custom_addon || ''
  } : {
    methodology: 'Correlation',
    subscription_level: 'bronze',
    commission_rate: 'R1',
    commission_custom: '',
    profit_rate: 'PR0',
    profit_custom: '',
    bot_level: 'BL1',
    bot_custom: '',
    ai_enabled: 'yes',
    subscription_length: 'monthly',
    monthly_price: '',
    yearly_price: '',
    custom_addon: '',
    is_active: 1
  });

  // ✅ ADD THESE STATE VARIABLES FOR DYNAMIC DROPDOWNS
  const [commissionRates, setCommissionRates] = useState([]);
  const [profitRates, setProfitRates] = useState([]);
  const [botLevels, setBotLevels] = useState([]);

  // ✅ ADD THIS useEffect TO LOAD LOOKUP TABLES FROM DATABASE
  useEffect(() => {
    const loadLookupTables = async () => {
      try {
        const token = localStorage.getItem('sessionToken');
        const headers = { Authorization: `Bearer ${token}` };
        
        const [commRes, profRes, botRes] = await Promise.all([
          axios.get('https://trading.omniscalping.com/api/admin/lookup/commission-rates', { headers }),
          axios.get('https://trading.omniscalping.com/api/admin/lookup/profit-rates', { headers }),
          axios.get('https://trading.omniscalping.com/api/admin/lookup/bot-levels', { headers })
        ]);

        setCommissionRates(commRes.data.rates);
        setProfitRates(profRes.data.rates);
        setBotLevels(botRes.data.levels);
      } catch (err) {
        console.error('Failed to load lookup tables:', err);
      }
    };

    loadLookupTables();
  }, []);

  // Auto-generate package code whenever relevant fields change
  const generatePackageCode = () => {
    const methodMap = {
      'Correlation': 'Corr',
      'Meme': 'Meme',
      'Scalping': 'Scal',
      'Turtle': 'Turt',
      'Stocks': 'Stoc'
    };
    
    const levelMap = {
      'bronze': 'B',
      'silver': 'S',
      'gold': 'G',
      'platinum': 'P'
    };

    const lengthMap = {
      'monthly': 'M',
      'yearly': 'Y'
    };

    const parts = [
      methodMap[formData.methodology] || 'Unkn',
      lengthMap[formData.subscription_length] || 'M',
      levelMap[formData.subscription_level] || 'B',
      formData.commission_rate,
      formData.profit_rate,
      formData.bot_level
    ];

    // Add AI indicator
    if (formData.ai_enabled === 'yes') {
      parts.push('AI');
    }

    // Add custom addon if provided
    if (formData.custom_addon && formData.custom_addon.trim() !== '') {
      parts.push('C' + formData.custom_addon.trim().replace(/\s+/g, ''));
    }

    return parts.join('-');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Generate package code
    const packageCode = generatePackageCode();
    
    // ✅ UPDATED: Get values from lookup tables dynamically
    const commRate = commissionRates.find(r => r.rate_code === formData.commission_rate);
    const commissionPercent = formData.commission_rate === 'R5' 
      ? parseFloat(formData.commission_custom) || 0
      : commRate?.per_trade_percent || 0;

    const profRate = profitRates.find(r => r.rate_code === formData.profit_rate);
    const profitPercent = formData.profit_rate === 'PR5'
      ? parseFloat(formData.profit_custom) || 0
      : profRate?.profit_percent || 0;

    const botLvl = botLevels.find(b => b.level_code === formData.bot_level);
    const maxBots = formData.bot_level === 'BL5'
      ? parseInt(formData.bot_custom) || 0
      : botLvl?.max_bots || 0;

    const saveData = {
      package_code: packageCode,
      methodology: formData.methodology,
      subscription_level: formData.subscription_level,
      subscription_length: formData.subscription_length,
      ai_enabled: formData.ai_enabled,
      custom_addon: formData.custom_addon,
      ai_package: formData.ai_enabled === 'yes' ? 'Enabled' : 'Disabled',
      commission_rate: formData.commission_rate,
      commission_percent: commissionPercent,
      profit_rate: formData.profit_rate,
      profit_percent: profitPercent,
      bot_level: formData.bot_level,
      max_bots: maxBots,
      monthly_price: formData.subscription_length === 'monthly' ? parseFloat(formData.monthly_price) || 0 : 0,
      yearly_price: formData.subscription_length === 'yearly' ? parseFloat(formData.yearly_price) || 0 : 0,
      enabled_tools: '[]',
      max_daily_trades: 0,
      max_position_size: 0,
      max_leverage: 0,
      is_active: formData.is_active
    };

    onSave(saveData);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
    });
  };

  return (
    <div className="package-form-modal">
      <div className="modal-content">
        <h3>{pkg ? 'Edit Package' : 'Create New Package'}</h3>
        
        {/* Preview Package Code */}
        <div className="package-code-preview">
          <strong>Package Code Preview:</strong>
          <div className="preview-code">{generatePackageCode()}</div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-grid">
            
            {/* Methodology */}
            <div className="form-group">
              <label>Methodology *</label>
              <select name="methodology" value={formData.methodology} onChange={handleChange} required>
                <option value="Correlation">Correlation</option>
                <option value="Meme">Meme</option>
                <option value="Scalping">Scalping</option>
                <option value="Turtle">Turtle</option>
                <option value="Stocks">Stocks</option>
              </select>
            </div>

            {/* Subscription Level */}
            <div className="form-group">
              <label>Subscription Level *</label>
              <select name="subscription_level" value={formData.subscription_level} onChange={handleChange} required>
                <option value="bronze">Bronze</option>
                <option value="silver">Silver</option>
                <option value="gold">Gold</option>
                <option value="platinum">Platinum</option>
              </select>
            </div>

            {/* Commission Rate - DYNAMIC FROM DATABASE */}
            <div className="form-group">
              <label>Commission Rate *</label>
              <select name="commission_rate" value={formData.commission_rate} onChange={handleChange} required>
                {commissionRates.map(rate => (
                  <option key={rate.id} value={rate.rate_code}>
                    {rate.description}
                  </option>
                ))}
              </select>
            </div>

            {/* Custom Commission (only if R5) */}
            {formData.commission_rate === 'R5' && (
              <div className="form-group">
                <label>Custom Commission %</label>
                <input 
                  type="number" 
                  name="commission_custom" 
                  value={formData.commission_custom}
                  onChange={handleChange}
                  step="0.01"
                  placeholder="Enter percentage"
                />
              </div>
            )}

            {/* Profit Rate - DYNAMIC FROM DATABASE */}
            <div className="form-group">
              <label>Profit Rate *</label>
              <select name="profit_rate" value={formData.profit_rate} onChange={handleChange} required>
                {profitRates.map(rate => (
                  <option key={rate.id} value={rate.rate_code}>
                    {rate.description}
                  </option>
                ))}
              </select>
            </div>

            {/* Custom Profit (only if PR5) */}
            {formData.profit_rate === 'PR5' && (
              <div className="form-group">
                <label>Custom Profit %</label>
                <input 
                  type="number" 
                  name="profit_custom" 
                  value={formData.profit_custom}
                  onChange={handleChange}
                  step="0.01"
                  placeholder="Enter percentage"
                />
              </div>
            )}

            {/* Bot Level - DYNAMIC FROM DATABASE */}
            <div className="form-group">
              <label>Bot Level *</label>
              <select name="bot_level" value={formData.bot_level} onChange={handleChange} required>
                {botLevels.map(level => (
                  <option key={level.id} value={level.level_code}>
                    {level.description}
                  </option>
                ))}
              </select>
            </div>

            {/* Custom Bot Count (only if BL5) */}
            {formData.bot_level === 'BL5' && (
              <div className="form-group">
                <label>Custom Bot Count</label>
                <input 
                  type="number" 
                  name="bot_custom" 
                  value={formData.bot_custom}
                  onChange={handleChange}
                  placeholder="Enter number of bots"
                  min="0"
                />
              </div>
            )}

            {/* AI Enabled */}
            <div className="form-group">
              <label>AI Enabled *</label>
              <select name="ai_enabled" value={formData.ai_enabled} onChange={handleChange} required>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </div>

            {/* Subscription Length */}
            <div className="form-group">
              <label>Subscription Length *</label>
              <select name="subscription_length" value={formData.subscription_length} onChange={handleChange} required>
                <option value="monthly">Monthly</option>
                <option value="yearly">Yearly</option>
              </select>
            </div>

            {/* Monthly Price */}
            <div className="form-group">
              <label>Monthly Price</label>
              <input 
                type="number" 
                name="monthly_price" 
                value={formData.monthly_price}
                onChange={handleChange}
                step="0.01"
				min="0"
                placeholder="$0.00"
                disabled={formData.subscription_length === 'yearly'}                
              />
            </div>

            {/* Yearly Price */}
            <div className="form-group">
              <label>Yearly Price</label>
              <input 
                type="number" 
                name="yearly_price" 
                value={formData.yearly_price}
                onChange={handleChange}
                step="0.01"
				min="0"
                placeholder="$0.00"
                disabled={formData.subscription_length === 'monthly'}                
              />
            </div>

            {/* Custom Addon */}
            <div className="form-group full-width">
              <label>Custom Add-on (optional)</label>
              <input 
                type="text" 
                name="custom_addon" 
                value={formData.custom_addon}
                onChange={handleChange}
                placeholder="e.g., 1on1, Training, Premium"
              />
            </div>

            {/* Active Checkbox */}
            <div className="form-group full-width">
              <label>
                <input 
                  type="checkbox" 
                  name="is_active" 
                  checked={formData.is_active === 1}
                  onChange={handleChange}
                />
                Active
              </label>
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn-primary">Save</button>
            <button type="button" onClick={onCancel} className="btn-secondary">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ============================================================================
// USER DETAILS MODAL
// ============================================================================
const UserDetailsModal = ({ user, tokens, onClose, onRevokeToken, onRevokeAllTokens, onToggleBotAdmin }) => {
  return (
    <div className="user-details-modal">
      <div className="modal-content">
        <div className="modal-header">
          <h3>User Details</h3>
          <button onClick={onClose} className="close-btn">×</button>
        </div>

        <div className="user-info">
          <p><strong>ID:</strong> {user.user.id}</p>
          <p><strong>Email:</strong> {user.user.email || 'N/A'}</p>
          <p><strong>Discord ID:</strong> {user.user.discord_id || 'N/A'}</p>
          <p><strong>Admin:</strong> {user.user.is_admin ? '👑 Yes' : 'No'}</p>
          <p><strong>Bot admin:</strong> {user.user.is_bot_admin ? '🤖 Yes' : 'No'}</p>
          {onToggleBotAdmin && (
            <p>
              <button
                type="button"
                className="btn-small"
                onClick={() => onToggleBotAdmin(user.user.id, user.user.is_bot_admin)}
              >
                {user.user.is_bot_admin ? 'Remove Bot Admin' : 'Make Bot Admin'}
              </button>
            </p>
          )}
          <p><strong>Created:</strong> {new Date(user.user.created_at).toLocaleString()}</p>
        </div>

        <h4>Subscriptions ({user.subscriptions.length})</h4>
        <div className="subscriptions-list">
          {user.subscriptions.length === 0 ? (
            <p>No subscriptions</p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Package Code</th>
                  <th>Methodology</th>
                  <th>Level</th>
                  <th>ID</th>
                </tr>
              </thead>
              <tbody>
                {user.subscriptions.map(sub => (
                  <tr key={sub.id}>
                    <td>{sub.package_code}</td>
                    <td>{sub.methodology}</td>
                    <td>{sub.subscription_level}</td>
                    <td>{sub.id}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="tokens-header">
          <h4>Active Tokens ({tokens.length})</h4>
          {tokens.length > 0 && (
            <button 
              className="btn-danger"
              onClick={() => onRevokeAllTokens(user.user.id)}
            >
              Revoke All Tokens
            </button>
          )}
        </div>

        <div className="tokens-list">
          {tokens.length === 0 ? (
            <p>No active tokens</p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Device Name</th>
                  <th>Fingerprint</th>
                  <th>Created</th>
                  <th>Expires</th>
                  <th>Last Used</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {tokens.map(token => (
                  <tr key={token.id}>
                    <td>{token.device_name}</td>
                    <td>{token.device_fingerprint.substring(0, 8)}...</td>
                    <td>{new Date(token.created_at).toLocaleDateString()}</td>
                    <td>{new Date(token.expires_at).toLocaleDateString()}</td>
                    <td>{token.last_used ? new Date(token.last_used).toLocaleString() : 'Never'}</td>
                    <td>
                      <button 
                        className="btn-small btn-danger"
                        onClick={() => onRevokeToken(token.id)}
                      >
                        Revoke
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};




export default Admin;